import { clsx } from 'clsx';
import type { PropsWithChildren } from 'react';

type BadgeProps = PropsWithChildren<{
  color: Color;
  size?: Size;
  icon?: React.JSX.Element;
}>;

function Badge({ color, size = 'large', icon, children }: BadgeProps) {
  const colorClasses = colors[color];
  const sizeClasses = sizes[size];

  return (
    <span
      className={clsx(
        'inline-flex items-center justify-center gap-1.5 whitespace-nowrap font-normal',
        colorClasses.container,
        sizeClasses.container
      )}
    >
      {icon && (
        <span
          className={clsx(colorClasses.icon, sizeClasses.icon, 'fa-width-auto')}
        >
          {icon}
        </span>
      )}
      {children}
    </span>
  );
}

const colors = /*tw*/ {
  gray: {
    container: 'bg-gray-300 text-gray-900',
    icon: 'text-gray-700',
  },
  green: {
    container: 'bg-green-200 text-green-900',
    icon: 'text-green-700',
  },
  /**
   * NOTE: this follows the indigo badge colors used in the CRM Opt-In Page,
   * which differ from the indigo colors on the Badge component in Figma's
   * Turnip. This is because the CRM Opt-In design is the only one to use an
   * indigo badge so far. If we need indigo badges that use the "official"
   * colors, we will refactor the Opt-In page to do something different and
   * update the colors here.
   * See https://www.figma.com/design/6NMjjxsEzdfePhSp0C2BP6?node-id=33-1909&m=dev#876673161
   */
  indigo: {
    container: 'bg-indigo-200 text-indigo-900',
    icon: 'text-indigo-700',
  },
  red: {
    container: 'bg-red-100 text-red-900',
    icon: 'text-red-700',
  },
  yellow: {
    container: 'bg-yellow-100 text-yellow-900',
    icon: 'text-yellow-700',
  },
  purple: {
    container: 'bg-purple-100 text-purple-900',
    icon: 'text-purple-700',
  },
};
type Color = keyof typeof colors;

const sizes = /*tw*/ {
  small: {
    container: 'px-2.5 py-0.5 text-xs/4 rounded-[10px]',
    icon: 'text-3xs',
  },
  large: {
    container: 'min-w-[55px] px-3 py-0.5 text-sm/5 rounded-xl',
    icon: 'text-xs',
  },
};
type Size = keyof typeof sizes;

export { Badge };
export type { BadgeProps, Color };
